import IconBathroom from "../../data/Icons/leon-inmobiliaria-icons-badezimmer.svg";
import IconBedroom from "../../data/Icons/leon-inmobiliaria-icons-schlafzimmer.svg";
import IconPersons from "../../data/Icons/leon-inmobiliaria-icons-personen.svg";
import ImmoKeyFactsBadge from "./ImmoKeyFactsBadge";
import IconGarage from "../../data/Icons/leon-inmobiliaria-icons-garage.svg";
import IconPets from "../../data/Icons/leon-inmobiliaria-icons-hund.svg";
import IconTv from "../../data/Icons/leon-inmobiliaria-icons-tv.svg";
import IconMooring from "../../data/Icons/leon-inmobiliaria-icons-bootsanlegeplatz.svg";
import IconPool from "../../data/Icons/leon-inmobiliaria-icons-pool.svg";
import IconAirConditioning from "../../data/Icons/leon-inmobiliaria-icons-klimaanlage.svg";
import IconInternet from "../../data/Icons/leon-inmobiliaria-icons-wlan.svg";
import IconForward from "../../data/Icons/leon-inmobiliaria-icons-arrow-2-forward.svg";
import IconFenced from "../../data/Icons/leon-inmobiliaria-icons-geschlossen.svg";
import IconDishwasher from "../../data/Icons/leon-inmobiliaria-icons-spuelmaschine.svg";
import IconWashingMachine from "../../data/Icons/leon-inmobiliaria-icons-waschmaschine.svg";
import IconShutters from "../../data/Icons/leon-inmobiliaria-icons-rollladen.svg";
import IconBbqGrill from "../../data/Icons/leon-inmobiliaria-icons-grill.svg";
import IconLivingArea from "../../data/Icons/leon-inmobiliaria-icons-wohnflaeche.svg";
import IconPlotSize from "../../data/Icons/leon-inmobiliaria-icons-grundstueck.svg";
import Image from "next/image";
import {useEffect, useState} from "react";
import {useRouter} from "next/router";
import {useQuery} from "@tanstack/react-query";
import {ImageFetch, ImmoImageTypes} from "../../helpers/ImageHelpers";

import IconArrowDown from "../../data/Icons/leon-inmobiliaria-icons-arrow-1-down.svg";
import {mapPropertyType} from "../../helpers/MapPropertyTypes";
import {NoImageAvailablePlaceholder} from "../../data/StaticImages";
import {Filter} from "../../data/Filter/Filter.config";
import {fetchItem, getSeasonDates} from "../../data/ReactQueries";
import {enforceArray} from "../../helpers/FilterFunctions";
import {calcNormalRentPrice,} from "../CalculationHelper/CalcHelpers";
import {objectsAttributes} from "../../models/objects";
import {ObjectInterfaceWithCity} from "../../interfaces/ObjectInterface";
import {useTranslation} from "next-i18next";
import Button from "./Button";

interface ImmoCardPropsInterface {
	minimalMobileVersion?: boolean;
	sale?: boolean;
	object: ObjectInterfaceWithCity;
	isLastMinuteCard?: boolean;
}

export default function ImmoCardHome(props: ImmoCardPropsInterface) {
	const {isLastMinuteCard} = props;

	const {t} = useTranslation();
	const {data: images, isLoading: isLoadingImages} = useQuery(
		["images", props.object.id],
		async () => {
			const resp = await ImageFetch(props.object.id, ImmoImageTypes.IMAGE);
			let tmpImages = await resp.json();
			tmpImages = [...tmpImages.main, ...tmpImages.others];
			return tmpImages;
		},
		{
			initialData: [NoImageAvailablePlaceholder],
		}
	);
    
	const {data: seasonDates, isLoading} = useQuery(
		["seasonDates"],
		getSeasonDates
	);

	const [isHoveringDetails, setIsHoveringDetails] = useState(false);
	const [currImage, setCurrImage] = useState(0);
	const [lastMinutePricePerWeek, setLastMinutePricePerWeek] = useState(0);
	const [normalPricePerWeek, setNormalPricePerWeek] = useState(0);
	const [startDate, setStartDate] = useState<Date>();
	const [endDate, setEndDate] = useState<Date>();
	const router = useRouter();
	const {data: lastMinutes} = useQuery(
		["lastMinutes", {[Filter.Rent.queryKey]: 1, locale: router.locale}],
		(): Promise<objectsAttributes[]> => {
			return fetchItem(
				"last-minutes",
				{
					[Filter.Rent.queryKey]: 1,
				},
				router.locale
			);
		}
	);

	useEffect(() => {
		let _startDate = startDate;
		let _endDate = endDate;

		if (props.object && seasonDates) {
			const [_normalPrice, _specialPrice] = calcNormalRentPrice(
				props.object,
				seasonDates,
				_startDate,
				_endDate
			);

			// if no date choosen, set default range to 7 days
			if (!_startDate || !_endDate) {
				setNormalPricePerWeek(_normalPrice);
				setLastMinutePricePerWeek(_specialPrice);
			} else {
				setNormalPricePerWeek(
					_normalPrice + parseInt(props.object.end_cleaning)
				);
				setLastMinutePricePerWeek(
					_specialPrice > 0
						? _specialPrice + parseInt(props.object.end_cleaning)
						: _specialPrice
				);
			}
		}
	}, [props.object, seasonDates, startDate, endDate, lastMinutes]);

	useEffect(() => {
		if (router.isReady && router.query.from_date && router.query.to_date) {
			const fromDate = enforceArray(router.query.from_date);
			const toDate = enforceArray(router.query.to_date);
			setStartDate(new Date(fromDate[0] * 1000));
			setEndDate(new Date(toDate[0] * 1000));
		}
	}, [router.query.from_date, router.query.to_date]);

	let innerSalePriceTag = <></>;
	if (props.sale) {
		if (props.object?.sales_price_gross_eur_special) {
			innerSalePriceTag = (
				<div className="flex flex-col">
					<div className="flex gap-2">
						<span className="font-bold text-[18px] sm:text-[26px]">
							{t("common.price", {
								price: parseInt(
									props.object.sales_price_gross_eur_special
								).toLocaleString(router.locale),
							})}
						</span>
						<div
							className={`inline-block font-bold text-leo-text-gray  line-through decoration-2 text-[12px] sm:text-[16px]`}
						>
							{t("common.price", {
								price: props.object.sales_price_gross_eur.toLocaleString(
									router.locale
								),
							})}
						</div>
					</div>
					<p className="text-[12px] sm:text-[18px] uppercase mb-2">
						{t("common.purchasePrice")}
					</p>
				</div>
			);
		} else {
			innerSalePriceTag = (
				<div className="flex flex-col">
					<span className="font-bold text-[28px]">
						{t("common.price", {
							price: props.object.sales_price_gross_eur.toLocaleString(
								router.locale
							),
						})}
					</span>
					<p className="text-[18px] uppercase mb-2">
						{t("common.purchasePrice")}
					</p>
				</div>
			);
		}
	}
	const salePriceTag = (
		<div className="flex grow justify-between">
			{props.object.status_sale === "Sold" ? <div></div> : innerSalePriceTag}
			{props.object.status_sale && props.object.status_sale !== "" && (
				<p className="font-bold">
					{t(`common.saleStatus.${props.object.status_sale}`)}
				</p>
			)}
		</div>
	);
	const noDateSelected = !startDate && !endDate;
	const showLastMinutePrice =
		lastMinutePricePerWeek != 0 && lastMinutePricePerWeek <= normalPricePerWeek;

	let calculatedPriceTag = t(
		noDateSelected ? "common.priceFrom" : "common.price",
		{
			price: showLastMinutePrice
				? lastMinutePricePerWeek.toLocaleString(router.locale)
				: normalPricePerWeek.toLocaleString(router.locale),
		}
	);

	let rentPriceTag = <></>;
	if (!props.sale) {
		if (isLastMinuteCard) {
			rentPriceTag = (
				<div className="flex grow justify-between items-center">
					<div className="flex flex-col">
						<p className="font-extrabold text-[18px] sm:text-[24px]">
							{t("common.price", {
								price: (
									(props.object.rent_special_price_eur_per_day ?? 0) * 7
								).toLocaleString(router.locale),
							})}
						</p>
						<p className="text-[12px] sm:text-[16px]">{t("components.immoCard.perWeek")}</p>
					</div>
					<div className="flex flex-col">
						<p className="font-extrabold text-[18px] sm:text-[24px] text-right">
							{t("components.vermietungDetailedTitle.lastMinutePrice")}
						</p>
						<p className="text-[12px] sm:text-[16px]">
							{new Date(
								props.object.rent_special_price_from!
							).toLocaleDateString(router.locale)}{" "}
							-{" "}
							{new Date(props.object.rent_special_price_to!).toLocaleDateString(
								router.locale
							)}
						</p>
					</div>
				</div>
			);
		} else if (showLastMinutePrice) {
			rentPriceTag = (
				<>
					<div
						className={`w-full font-bold text-leo-text-gray  line-through decoration-2 ${
							noDateSelected ? "ml-5 lg:ml-6" : "ml-0 lg:ml-0"
						}`}
					>
						{t("common.price", {
							price: normalPricePerWeek.toLocaleString(router.locale),
						})}
					</div>
					<div className="font-bold flex justify-between grow">
						<p>{`${calculatedPriceTag} ${
							noDateSelected ? "/ " + t("components.immoCard.week") : ""
						}`}</p>
						<div className="block lg:hidden">
							{t("components.immoCard.lastMinutePrice")}
						</div>
					</div>
					<div className="hidden justify-between lg:flex font-bold">
						<div className="hidden lg:block">
							{t("components.immoCard.lastMinutePrice")}
						</div>
					</div>
				</>
			);
		} else {
			rentPriceTag = (
				<>
					<div className="w-full">
						<br/>
					</div>
					<div className="font-bold flex gap-5">{calculatedPriceTag}</div>
					<div className="hidden gap-16 lg:flex font-bold">
						{noDateSelected ? (
							<div>&nbsp;/ {t("components.immoCard.week")}</div>
						) : (
							<div></div>
						)}
					</div>
				</>
			);
		}
	}

	const goToObject = () => {
		if (props.sale) {
			router.push(`/verkauf/${props.object.id}`);
		} else {
			router.push({
				pathname: `/vermietung/${props.object.id}`,
				query: router.query,
			});
		}
		return;
	};
	return (
		<div
			className={`mb-4 ${
				props.minimalMobileVersion
					? "w-[225px] sm:w-full"
					: "w-full sm:max-w-[500px] md:w-[320px] h-[656px]"
			} h-[550px] sm:h-[680px] lg:flex-1 flex flex-col cursor-pointer p-2 lg:p-[15px]`}
			onClick={goToObject}
			onMouseEnter={() => setIsHoveringDetails(true)}
			onMouseLeave={() => setIsHoveringDetails(false)}
		>
			<div
				className={`w-full block relative flex-col items-end justify-between bg-cover h-[160px] sm:h-[260px] lg:h-[265px] group`}
				/* style={{ backgroundImage: isLoadingImages ? ""  : `url(/_next/image?url=${images[currImage]}&w=640&q=75)` }} */
			>
				{isLoadingImages === false && (
					<div>
						{images.map((image: string, idx: number) => (
							<Image
								quality={90}
								src={image ?? NoImageAvailablePlaceholder}
								key={
									idx +
									"-" +
									props.object.id +
									"-" +
									(image ?? NoImageAvailablePlaceholder)
								}
								alt="Leon Inmobiliaria"
								fill
								sizes="(max-width: 768px) 80vw,(max-width: 1200px) 50vw,30vw"
								placeholder="blur"
								blurDataURL={NoImageAvailablePlaceholder}
								loading="lazy"
								style={{
									objectFit: "cover",
									display: idx === currImage ? "block" : "none",
								}}
								className="rounded-tl-[20px] rounded-tr-[20px]"
							/>
						))}
						{props.object?.["360tour"] && props.object?.["360tour"] != "" && (
							<div
								className="absolute bottom-0 right-0 m-4"
								onMouseEnter={() => setIsHoveringDetails(false)}
								onMouseLeave={() => {
									setIsHoveringDetails(true);
								}}
							>
								<Button
									onClick={(e) => {
										e.preventDefault();
										e.stopPropagation();
										window.open(
											props.object?.["360tour"]
										);
									}}
									border={false}
									text={t("components.immoCard.openVirtualTour")}
									transparent={false}
								/>
							</div>
						)}
					</div>
				)}

				<div className="w-full justify-between relative top-2/4 hidden group-hover:flex">
					<button
						className=" z-0 text-white lg:h-10 lg:w-10 h-14 w-14 rotate-90 lg:opacity-60 opacity-80 hover:opacity-90 backdrop-brightness-75 p-1"
						onClick={(e) => {
							e.preventDefault();
							e.stopPropagation();
							if (currImage === 0) {
								setCurrImage(images.length - 1);
							} else {
								setCurrImage(currImage - 1);
							}
						}}
						onMouseEnter={() => setIsHoveringDetails(false)}
						onMouseLeave={() => {
							setIsHoveringDetails(true);
						}}
					>
						<IconArrowDown/>
					</button>
					<button
						className=" z-0 text-white lg:h-10 lg:w-10 h-14 w-14 -rotate-90 lg:opacity-60 opacity-80 hover:opacity-90 backdrop-brightness-75 p-1"
						onClick={(e) => {
							e.preventDefault();
							e.stopPropagation();
							if (currImage + 1 < images.length) {
								setCurrImage(currImage + 1);
							} else {
								setCurrImage(0);
							}
						}}
						onMouseEnter={() => setIsHoveringDetails(false)}
						onMouseLeave={() => {
							setIsHoveringDetails(true);
						}}
					>
						<IconArrowDown/>
					</button>
				</div>
				<div className="w-full flex justify-end gap-2 p-4">
					{/* <button>
            <div className="bg-leo-light-gray text-leo-button-gray hover:text-leo-orange transition duration-300 rounded-full w-6 h-6 p-0.5">
              <IconCompare />
            </div>
          </button>
          <button>
            <div className="bg-leo-light-gray text-leo-button-gray hover:text-leo-orange transition duration-300 rounded-full w-6 h-6 p-0.5">
              <IconLike />
            </div>
          </button> */}
				</div>
				<div className="text-white p-4">
					{currImage + 1}/{images.length}
				</div>
			</div>
			<div
				className=" h-1 w-full"
				style={{
					background: `linear-gradient(90deg, #F1F1F1 ${
						(100 / images.length) * currImage
					}%,#43c4f7 0 ${(100 / images.length) * (currImage + 1)}%, #F1F1F1 ${
						(100 / images.length) * (currImage + 1)
					}%)`,
				}}
			/>
			<div className="flex-col flex grow">
				<div className="justify-between flex flex-col px-2 lg:px-4 pt-6 pb-4 grow">
					<div>
						<div className="flex gap-1 lg:gap-2">
							<div className=" flex gap-2">
								{props.object.persons_total > 0 && !props.sale && (
									<ImmoKeyFactsBadge
										icon={<IconPersons/>}
										count={props.object.persons_total}
									/>
								)}
								{props.object.bedrooms_total > 0 && (
									<ImmoKeyFactsBadge
										icon={<IconBedroom/>}
										count={props.object.bedrooms_total}
									/>
								)}
								{props.object.bathrooms_total > 0 && (
									<ImmoKeyFactsBadge
										icon={<IconBathroom/>}
										count={props.object.bathrooms_total}
									/>
								)}
							</div>
							{props.sale && (
								<div className="flex grow lg:gap-1">
									<div className="flex gap-1 sm:pt-2 pr-1 lg:pr-2 items-center">
										<div className="h-3 w-3 sm:h-5 sm:w-5">
											<IconLivingArea/>
										</div>
										<div className="text-[8px] sm:text-[12px]">
											{t("circa")}&nbsp;{props.object.living_area_m2} m²
										</div>
									</div>
									<div className="flex gap-1 sm:pt-2 pr-1 lg:pr-2 items-center">
										<div className="h-3 w-3 sm:h-5 sm:w-5">
											<IconPlotSize/>
										</div>
										<div className="text-[8px] sm:text-[12px]">
											{t("circa")}&nbsp;{props.object.plot_size} m²
										</div>
									</div>
								</div>
							)}
						</div>
						<h4 className="text-[22px] sm:text-[30px]">
							{props.object.cityOfObject.name}
							{mapPropertyType(props.object) &&
								` / ${t(mapPropertyType(props.object)!)}`}
						</h4>
					</div>
					<p
						className={`leading-[1.3] text-[12px] sm:text-[14px] truncate-text-3-lines `}
					>
						{props.object.headline}
					</p>
					<div className={`flex flex-col lg:flex-row flex-wrap  ${props.sale ? "text-leo-brown" : "text-leo-blue" }`}>
						{props.sale && salePriceTag}
						{rentPriceTag}
					</div>
					<div>
						<div
							className={` gap-1 [&>*]:h-5 text-leo-button-gray flex-wrap flex`}
						>
							{props.object.garage >= 1 && (
								<div className="w-4">
									<IconGarage/>
								</div>
							)}
							{props.object.pets === 1 && (
								<div className="w-4">
									<IconPets/>
								</div>
							)}
							{Boolean(props.object.cable_sat_tv) && (
								<div className="w-4">
									<IconTv/>
								</div>
							)}
							{props.object.mooring === 1 && (
								<div className="w-4">
									<IconMooring/>
								</div>
							)}
							{Boolean(
								props.object.private_pool ||
								props.object.indoor_pool ||
								props.object.outdoor_pool ||
								props.object.communal_pool
							) && (
								<div className="w-4">
									<IconPool/>
								</div>
							)}
							{props.object.aircondition >= 1 && (
								<div className="w-4">
									<IconAirConditioning/>
								</div>
							)}
							{props.object.internet === 1 && (
								<div className="w-4">
									<IconInternet/>
								</div>
							)}
							{props.object.fenced_yard === 1 && (
								<div className="w-4">
									<IconFenced/>
								</div>
							)}
							{Boolean(props.object.dishwasher) && (
								<div className="w-4">
									<IconDishwasher/>
								</div>
							)}
							{Boolean(props.object.washing_machine) && (
								<div className="w-4">
									<IconWashingMachine/>
								</div>
							)}
							{Boolean(props.object.shutters) && (
								<div className="w-4">
									<IconShutters/>
								</div>
							)}
							{Boolean(props.object.bbq_grill) && (
								<div className="w-4">
									<IconBbqGrill/>
								</div>
							)}
						</div>
						<div className="mt-2">
							<div className="text-[#a3a3a3] text-[10px] sm:text-[12px] leading-[1.2] break-all">{props.object.tln && `${props.object.tln} /`}{" "}</div>
							<div className="text-[#3c3c3b] font-semibold text-[12px] sm:text-[14px] leading-[1.2] break-all">{t("components.immoCard.objectId")}: {props.object.object_id}</div>
						</div>
					</div>
				</div>
				<button
					className={`h-10 sm:h-12 w-full max-w-[140px] sm:max-w-[200px] items-center text-sm text-white sm:px-6 justify-center flex lg:gap-2 rounded-[10px] self-end ${
						isHoveringDetails ? "" : ""
					}
					${props.sale ? "bg-gradient-to-b from-[#b6aa9b] to-[#857a6d] hover:bg-leo-dark-brown hover:from-transparent hover:to-transparent transition-all duration-300" : "bg-gradient-to-b from-[#a8e1f7] to-[#43c4f7] hover:bg-leo-dark-brown hover:from-transparent hover:to-transparent transition-all duration-300"}
					`}
					onClick={goToObject}
				>
					<div className="flex items-center justify-center gap-2 pr-2 uppercase font-bold text-center w-full">
						<span className="text-[12px] sm:text-[16px]">{t("components.immoCard.details")}</span>
					</div>
				</button>
			</div>
		</div>
	);
}
